import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Loader2, Plus, ImageIcon, Video, Music, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_MS = 60 * 60 * 1000; // 60 minutes (videos can take a while on KIE)

// Human-readable status text
function statusLabel(status) {
  switch (status) {
    case 'queued':      return 'Task submitted to KIE...';
    case 'waiting':     return 'In queue on KIE — waiting for a GPU slot...';
    case 'queuing':     return 'Preparing task on KIE...';
    case 'generating':  return 'Generating — this can take a few minutes...';
    default:            return status || 'Working...';
  }
}

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r.toString().padStart(2, '0')}s` : `${r}s`;
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

/** Reusable uploader block that returns URLs to the parent. */
function UploaderBlock({ label, urls, setUrls, max, accept, kindIcon: KindIcon }) {
  const [busy, setBusy] = useState(false);
  const disabled = urls.length >= max;

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      setBusy(true);
      const res = await axios.post(`${API_URL}/generation/upload`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setUrls((prev) => [...prev, res.data.url]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (urls.length >= max) break;
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(f);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm text-zinc-300 flex items-center gap-2">
          <KindIcon className="w-4 h-4" strokeWidth={1.5} />
          {label} <span className="text-zinc-500">({urls.length}/{max})</span>
        </label>
        <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border cursor-pointer
          ${disabled ? 'border-zinc-800 text-zinc-600 cursor-not-allowed' : 'border-zinc-700 text-zinc-200 hover:bg-zinc-900'}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add file
          <input type="file" accept={accept} className="hidden" onChange={onPick} disabled={disabled || busy} multiple />
        </label>
      </div>
      {urls.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {urls.map((u, i) => (
            <div key={u + i} className="relative group border border-zinc-800 rounded-md p-2 bg-zinc-950">
              <div className="text-[10px] text-zinc-500 break-all line-clamp-2">{u.split('/').pop()}</div>
              <button
                onClick={() => setUrls((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 p-1 rounded bg-black/70 border border-zinc-700 opacity-0 group-hover:opacity-100 transition"
                data-testid="remove-upload-btn"
              >
                <X className="w-3 h-3 text-zinc-300" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultPreview({ gen }) {
  if (!gen) return null;
  if (gen.status === 'success' && (gen.result_urls || []).length) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {gen.result_urls.map((u, i) => (
          <div key={u + i} className="rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950">
            {gen.kind === 'video' ? (
              <video src={u} controls className="w-full h-auto" />
            ) : (
              <img src={u} alt={`result-${i}`} className="w-full h-auto" />
            )}
            <div className="p-2 flex justify-between items-center text-xs">
              <span className="text-zinc-500">Result {i + 1}</span>
              <a href={u} target="_blank" rel="noreferrer" className="text-white underline">Open</a>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (gen.status === 'fail') {
    return <div className="text-sm text-red-400">Failed: {gen.error || 'Unknown error'}</div>;
  }
  const startedAt = gen.created_at ? new Date(gen.created_at).getTime() : null;
  return <ProgressStatus status={gen.status} startedAt={startedAt} kind={gen.kind} />;
}

/** Live status line with elapsed timer that updates each second. */
function ProgressStatus({ status, startedAt, kind }) {
  const [elapsed, setElapsed] = useState(startedAt ? Date.now() - startedAt : 0);
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  const label = statusLabel(status);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-zinc-200">
        <Loader2 className="w-4 h-4 animate-spin text-white" />
        <span>{label}</span>
      </div>
      {startedAt && (
        <div className="text-xs text-zinc-500 pl-6">
          Elapsed: <span className="text-zinc-300 font-mono">{formatElapsed(elapsed)}</span>
          {kind === 'video' && ' · videos typically take 2–8 min'}
        </div>
      )}
    </div>
  );
}

export default function GenerateTab({ onGenerated }) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('nano-banana-pro');
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [resolution, setResolution] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [duration, setDuration] = useState(5);
  const [generateAudio, setGenerateAudio] = useState(false);
  const [nsfwChecker, setNsfwChecker] = useState(true);
  const [imageInput, setImageInput] = useState([]);       // Nano Banana Pro
  const [refImages, setRefImages] = useState([]);         // Seedance ref image urls
  const [refVideos, setRefVideos] = useState([]);         // Seedance ref video urls
  const [refAudios, setRefAudios] = useState([]);         // Seedance ref audio urls
  const [busy, setBusy] = useState(false);
  const [currentGen, setCurrentGen] = useState(null);

  // Load model catalog + resume any pending generation from localStorage
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/generation/models`, { headers: authHeaders() });
        setModels(res.data.models || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load models');
      }
    };
    load();

    // Resume polling if we have a pending generation stored
    const pending = localStorage.getItem('generation:pending');
    if (pending) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/generation/task/${pending}`, { headers: authHeaders() });
          setCurrentGen(res.data);
          const s = res.data.status;
          if (s !== 'success' && s !== 'fail') {
            setBusy(true);
            const done = await pollGeneration(pending);
            setBusy(false);
            if (done?.status === 'success') toast.success('Generation completed');
            else if (done?.status === 'fail') toast.error(`Generation failed: ${done.error || 'Unknown'}`);
          } else {
            localStorage.removeItem('generation:pending');
          }
        } catch (e) {
          localStorage.removeItem('generation:pending');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const model = useMemo(() => models.find((m) => m.id === modelId), [models, modelId]);

  // Reset per-model options when switching
  useEffect(() => {
    if (!model) return;
    setAspectRatio(model.aspect_ratios?.[0] || '');
    setResolution(model.resolutions?.[0] || '');
    setOutputFormat(model.output_formats?.[0] || '');
    if (model.kind === 'video') {
      setDuration(model.durations?.[0] || 5);
    }
    setImageInput([]);
    setRefImages([]);
    setRefVideos([]);
    setRefAudios([]);
  }, [modelId, model]);

  const estimatedCost = useMemo(() => {
    if (!model) return 0;
    if (model.kind === 'image') return model.pricing?.[resolution] || 0;
    if (model.kind === 'video') {
      const pricing = model.pricing_per_second;
      const dur = parseInt(duration || 0, 10);
      let rate = 0;
      if (pricing && typeof pricing === 'object') {
        const tier = pricing[(resolution || '480p').toLowerCase()] || pricing['480p'];
        if (tier && typeof tier === 'object') {
          rate = refVideos.length > 0 ? (tier.with_video || 0) : (tier.no_video || 0);
        } else if (typeof tier === 'number') {
          rate = tier;
        }
      } else if (typeof pricing === 'number') {
        rate = pricing;
      }
      return rate * dur;
    }
    return 0;
  }, [model, resolution, duration, refVideos.length]);

  const pollGeneration = async (generationId) => {
    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const res = await axios.get(`${API_URL}/generation/task/${generationId}`, { headers: authHeaders() });
        setCurrentGen(res.data);
        const s = res.data.status;
        if (s === 'success' || s === 'fail') {
          localStorage.removeItem('generation:pending');
          return res.data;
        }
      } catch (e) {
        // keep polling, but stop if unauthorized
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem('generation:pending');
          return null;
        }
      }
      if (Date.now() - start > POLL_MAX_MS) {
        toast.warning('Still processing, check History tab later');
        return null;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  const submit = async () => {
    if (!prompt.trim()) {
      toast.error('Prompt is required');
      return;
    }
    if (!model) return;
    if (prompt.length > model.prompt_max) {
      toast.error(`Prompt too long (max ${model.prompt_max})`);
      return;
    }

    const payload = {
      model: model.id,
      prompt,
      aspect_ratio: aspectRatio || undefined,
      resolution: resolution || undefined,
    };
    if (model.output_formats?.length && outputFormat) payload.output_format = outputFormat;
    if (model.id === 'nano-banana-pro' && imageInput.length) payload.image_input = imageInput;

    if (model.kind === 'video') {
      payload.duration = parseInt(duration, 10);
      payload.generate_audio = !!generateAudio;
      if (model.supports_nsfw_toggle) payload.nsfw_checker = !!nsfwChecker;
      if (refImages.length) payload.reference_image_urls = refImages;
      if (refVideos.length) payload.reference_video_urls = refVideos;
      if (refAudios.length) payload.reference_audio_urls = refAudios;
    }

    try {
      setBusy(true);
      const res = await axios.post(`${API_URL}/generation/create`, payload, { headers: authHeaders() });
      setCurrentGen(res.data);
      // Persist so polling can resume if user switches tab or refreshes
      localStorage.setItem('generation:pending', res.data.generation_id);
      toast.success(`${model.label} generation started`);
      const done = await pollGeneration(res.data.generation_id);
      if (done?.status === 'success') {
        toast.success('Generation completed');
      } else if (done?.status === 'fail') {
        toast.error(`Generation failed: ${done.error || 'Unknown'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start generation');
    } finally {
      setBusy(false);
    }
  };

  if (!models.length) {
    return <div className="text-sm text-zinc-400">Loading models...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column: form */}
      <div className="lg:col-span-2 space-y-4">
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5 space-y-5">
            {/* Model selector */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-300">Model</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {models.map((m) => {
                  const active = modelId === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setModelId(m.id)}
                      data-testid={`model-${m.id}`}
                      className={`text-left p-3 rounded-md border transition ${
                        active
                          ? 'border-white bg-white text-black'
                          : 'border-zinc-800 bg-zinc-950 text-zinc-200 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-semibold text-sm">{m.label}</div>
                      <div className={`text-xs ${active ? 'text-zinc-700' : 'text-zinc-500'}`}>
                        {m.kind === 'video' ? 'Video' : 'Image'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Prompt */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="text-sm text-zinc-300">Prompt</label>
                <span className="text-xs text-zinc-500">
                  {prompt.length}/{model?.prompt_max || 20000}
                </span>
              </div>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to generate..."
                rows={5}
                className="bg-zinc-950 border-zinc-800 text-white resize-y"
                data-testid="generation-prompt"
              />
            </div>

            {/* Settings grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {model?.aspect_ratios?.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm text-zinc-300">Aspect ratio</label>
                  <Select value={aspectRatio} onValueChange={setAspectRatio}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="aspect-ratio-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {model.aspect_ratios.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {model?.resolutions?.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm text-zinc-300">Resolution / Quality</label>
                  <Select value={resolution} onValueChange={setResolution}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="resolution-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {model.resolutions.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {model?.output_formats?.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm text-zinc-300">Output format</label>
                  <Select value={outputFormat} onValueChange={setOutputFormat}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="output-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {model.output_formats.map((v) => (
                        <SelectItem key={v} value={v}>{v.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {model?.kind === 'video' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm text-zinc-300">Duration (seconds)</label>
                    <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {model.durations.map((v) => (
                          <SelectItem key={v} value={String(v)}>{v}s</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-zinc-300">Audio</label>
                    <div className="flex items-center gap-3 h-10 px-3 bg-zinc-950 border border-zinc-800 rounded-md">
                      <input
                        id="gen-audio"
                        type="checkbox"
                        checked={generateAudio}
                        onChange={(e) => setGenerateAudio(e.target.checked)}
                        className="accent-white"
                        data-testid="generate-audio-toggle"
                      />
                      <label htmlFor="gen-audio" className="text-sm text-zinc-300 cursor-pointer">
                        Generate audio track
                      </label>
                    </div>
                  </div>
                  {model.supports_nsfw_toggle && (
                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-sm text-zinc-300">NSFW checker</label>
                      <div className="flex items-center gap-3 h-10 px-3 bg-zinc-950 border border-zinc-800 rounded-md">
                        <input
                          id="nsfw-checker"
                          type="checkbox"
                          checked={nsfwChecker}
                          onChange={(e) => setNsfwChecker(e.target.checked)}
                          className="accent-white"
                          data-testid="nsfw-checker-toggle"
                        />
                        <label htmlFor="nsfw-checker" className="text-sm text-zinc-300 cursor-pointer flex-1">
                          Enable NSFW content filter
                          <span className="block text-xs text-zinc-500">
                            Uncheck to disable safety filtering (allows adult content)
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Uploads */}
            {model?.id === 'nano-banana-pro' && model.image_input_max > 0 && (
              <UploaderBlock
                label="Input images (for image-to-image)"
                urls={imageInput}
                setUrls={setImageInput}
                max={model.image_input_max}
                accept="image/*"
                kindIcon={ImageIcon}
              />
            )}

            {model?.kind === 'video' && (
              <div className="space-y-4">
                <UploaderBlock
                  label="Reference images"
                  urls={refImages}
                  setUrls={setRefImages}
                  max={model.image_input_max || 9}
                  accept="image/*"
                  kindIcon={ImageIcon}
                />
                <UploaderBlock
                  label="Reference videos"
                  urls={refVideos}
                  setUrls={setRefVideos}
                  max={model.video_input_max || 3}
                  accept="video/*"
                  kindIcon={Video}
                />
                <UploaderBlock
                  label="Reference audio"
                  urls={refAudios}
                  setUrls={setRefAudios}
                  max={model.audio_input_max || 3}
                  accept="audio/*"
                  kindIcon={Music}
                />
              </div>
            )}

            {/* Submit */}
            <div className="flex items-center justify-between pt-2 border-t border-zinc-800 gap-3 flex-wrap">
              <div className="text-sm text-zinc-400">
                Est. cost: <span className="text-white font-semibold">~${estimatedCost.toFixed(3)}</span>
                {model?.kind === 'video' && (
                  <span className="text-xs text-zinc-500 ml-2">
                    ({refVideos.length > 0 ? 'discounted — reference video provided' : 'standard rate — no reference video'})
                  </span>
                )}
                <div className="text-xs text-zinc-500 mt-0.5">
                  Estimate only · reference images do NOT reduce cost · not charged on fail
                </div>
              </div>
              <Button
                onClick={submit}
                disabled={busy || !prompt.trim()}
                className="bg-white text-black hover:bg-zinc-200"
                data-testid="generate-btn"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right column: current result */}
      <div className="space-y-4">
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Current generation</h3>
              {currentGen && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                  {currentGen.status}
                </Badge>
              )}
            </div>
            {currentGen ? (
              <>
                <div className="text-xs text-zinc-500">
                  {currentGen.model_label} · Cost: ${(currentGen.cost_usd || 0).toFixed(3)}
                </div>
                <ResultPreview gen={currentGen} />
                {currentGen.status === 'success' && (
                  <Button
                    variant="outline"
                    onClick={onGenerated}
                    className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                    data-testid="view-history-btn"
                  >
                    View in History
                  </Button>
                )}
              </>
            ) : (
              <div className="text-sm text-zinc-500">No generation yet. Fill the form and press Generate.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
